import React, { useEffect, useRef, useState } from 'react'
import {
  Box,
  Checkbox,
  Fade,
  FormControlLabel,
  Typography,
} from '@material-ui/core'
import { connect } from 'react-redux'
import { useRouter } from 'next/router'
import { Cart, GAEvent } from '@/typings/base'
import {
  PayPalButtons,
  PayPalButtonsComponentProps,
} from '@paypal/react-paypal-js'
import Link from '../Link/Link'
import CMSText from '../Global/CMSText'
import useCommonStyles from './CardForm.style'
import { useMatomo } from '@datapunt/matomo-tracker-react'
import { checkStockQuantityByItemType, numberToPrice } from '@/utils/utils'
import * as gtag from '../../utils/gtag'
import {
  clearItemVariantFromGAParamsIfNotChalhoub,
  getPaymentInfoGtmData,
  isChalhoub,
  safeDataLayerPush,
} from '@/utils/gtag'
import { createCheckoutPayment, getCheckout } from '@/redux/actions/checkout'
import { postFinalizeDadPayment } from '@/redux/actions/payment'
import { datadogLogs } from '@datadog/browser-logs'
import { useTranslation } from 'react-i18next'
import { PrismicShop } from '@/interfaces/common/prismicShop'
import useRetractationEffect from './hook/useRetractationEffect'
import { isSubscription } from '@/utils/constants'
import { getStock, getUniqueProductStock } from '@/redux/services/products'
import dayjs from 'dayjs'

type Tprops = {
  cart: Cart
  doc: PrismicShop
  postFinalizeDadPayment: (data: object, token: string) => void
  createCheckoutPayment: (token: string) => void
  startLeasingDate: string
  coupon?: string
  stores: any
}

/** Result of the RMS availability check before creating a PayPal order */
type PrePaypalAvailabilityCheckResult =
  | { ok: true }
  | { ok: false; reason: 'insufficient_stock'; products: string[] }
  | { ok: false; reason: 'availability_check_unavailable' }

const PaymentPaypal = ({
  cart,
  doc,
  postFinalizeDadPayment,
  createCheckoutPayment,
  startLeasingDate,
  coupon,
  stores,
}: Tprops) => {
  const commonClasses = useCommonStyles()
  const router = useRouter()
  const { t } = useTranslation()
  const [retractReady, setRetractReady] = useState(false)
  const [retractAgree, setRetractAgree] = useState(false)
  const [cgu, setCgu] = useState(false)
  const [error, setError] = useState('')
  /** Cart lines that exceed current RMS availability */
  const [insufficientStockProducts, setInsufficientStockProducts] = useState<
    string[] | null
  >(null)
  /** Network / invalid response / RMS error — not a “stock shortage”, but payment stays blocked */
  const [availabilityCheckFailed, setAvailabilityCheckFailed] = useState(false)
  const isRetractation = doc?.data?.enable_retractation_agreement
  const retractingDays = doc?.data?.retractation_duration
  const { trackEvent, pushInstruction } = useMatomo()
  const [registeredStoreSlug, setRegisteredStoreSlug] = useState('')
  /** PayPal onError may run before React commits state from createOrder */
  const prePaypalPaymentBlockedRef = useRef(false)

  const isPaypalBlocked =
    insufficientStockProducts !== null || availabilityCheckFailed

  useEffect(() => {
    typeof localStorage !== 'undefined' &&
      setRegisteredStoreSlug(
        JSON.parse(localStorage.getItem('selectedStore'))?.slug
      )
  }, [])

  useRetractationEffect({
    startLeasingDate,
    isRetractation,
    retractingDays,
    setRetractReady,
    setRetractAgree,
  })

  const changeRetract = () => {
    setRetractAgree(!retractAgree)
  }

  const changeCgu = () => {
    setCgu(!cgu)
  }

  const checkAvailabilityBeforePaypal =
    async (): Promise<PrePaypalAvailabilityCheckResult> => {
      if (!cart?.item?.items) {
        return { ok: true }
      }

      const email =
        typeof localStorage !== 'undefined'
          ? localStorage.getItem('email')
          : null

      if (!email) {
        return { ok: false, reason: 'availability_check_unavailable' }
      }

      try {
        const cartPuids: { puid: string; productName: string }[] = []

        cart.item.items.forEach((item) => {
          item.units?.forEach((unit) => {
            if (unit?.puid) {
              cartPuids.push({
                puid: unit.puid,
                productName: item.product.name,
              })
            }
          })
        })

        // Seconde main: align with backend OrderManager — check each PUID via unique-products
        if (cartPuids.length > 0) {
          const uniquePuids = Array.from(
            new Set(cartPuids.map((entry) => entry.puid))
          )
          const stockResponse = await getUniqueProductStock(uniquePuids)

          if (!Array.isArray(stockResponse)) {
            return { ok: false, reason: 'availability_check_unavailable' }
          }

          const unavailablePuids = new Set(
            stockResponse
              .filter((item) => item.available === false)
              .map((item) => item.puid)
          )

          const outOfStockNames = Array.from(
            new Set(
              cartPuids
                .filter((entry) => unavailablePuids.has(entry.puid))
                .map((entry) => entry.productName)
            )
          )

          if (outOfStockNames.length > 0) {
            return {
              ok: false,
              reason: 'insufficient_stock',
              products: outOfStockNames,
            }
          }

          return { ok: true }
        }

        // Rental / purchase: align with backend — GET /api/stock with customer email
        const warehouseId = stores?.selectedStore?.warehouseId

        const SKUArray = cart.item.items.map(
          (item) => item.product.variants[0].sku
        )

        let data: any = { lizeeSKUs: SKUArray, email }

        if (
          cart.item?.startLeasingDate &&
          cart.item?.endLeasingDate &&
          !isSubscription
        ) {
          const fromDate = dayjs(
            cart.item.startLeasingDate,
            'DD/MM/YYYY'
          ).format('YYYY-MM-DD')
          const toDate = dayjs(cart.item.endLeasingDate, 'DD/MM/YYYY').format(
            'YYYY-MM-DD'
          )

          data = {
            ...data,
            from: fromDate,
            to: toDate,
          }
        }

        const stockResponse = await getStock(data, warehouseId)

        if (!Array.isArray(stockResponse)) {
          return { ok: false, reason: 'availability_check_unavailable' }
        }

        const outOfStockNames: string[] = []

        cart.item.items.forEach((item) => {
          const availability = checkStockQuantityByItemType(
            { stockBasket: stockResponse },
            item.product.variants[0],
            item.type as any,
            'basket'
          )

          if (item.quantity > (availability ?? 0)) {
            outOfStockNames.push(item.product.name)
          }
        })

        if (outOfStockNames.length > 0) {
          return {
            ok: false,
            reason: 'insufficient_stock',
            products: outOfStockNames,
          }
        }

        return { ok: true }
      } catch (error) {
        datadogLogs.logger.error(
          'PayPal pre-payment availability check failed',
          error
        )
        return { ok: false, reason: 'availability_check_unavailable' }
      }
    }

  const createOrder: PayPalButtonsComponentProps['createOrder'] = async (
    data,
    actions
  ) => {
    prePaypalPaymentBlockedRef.current = false
    setInsufficientStockProducts(null)
    setAvailabilityCheckFailed(false)

    const availability = await checkAvailabilityBeforePaypal()

    if (availability.ok === false) {
      prePaypalPaymentBlockedRef.current = true
      if (availability.reason === 'insufficient_stock') {
        setInsufficientStockProducts(availability.products)
      } else {
        setAvailabilityCheckFailed(true)
      }
      throw new Error('PayPal pre-payment blocked')
    }

    return await actions.order.create({
      intent: 'CAPTURE',
      purchase_units: [
        {
          amount: {
            currency_code: cart?.item?.currency,
            value: (cart?.item?.totals?.total / 100).toFixed(2),
          },
        },
      ],
    })
  }

  const onError: PayPalButtonsComponentProps['onError'] = (error) => {
    if (prePaypalPaymentBlockedRef.current || isPaypalBlocked) {
      prePaypalPaymentBlockedRef.current = false
      return
    }
    setError('An error occured, please try again')
    datadogLogs.logger.error('An error occured, please try again', error)
  }

  const onApprove: PayPalButtonsComponentProps['onApprove'] = async (
    data,
    actions
  ) => {
    prePaypalPaymentBlockedRef.current = false
    setInsufficientStockProducts(null)
    setAvailabilityCheckFailed(false)

    const availability = await checkAvailabilityBeforePaypal()
    if (availability.ok === false) {
      if (availability.reason === 'insufficient_stock') {
        setInsufficientStockProducts(availability.products)
      } else {
        setAvailabilityCheckFailed(true)
      }
      return
    }

    trackEvent({
      category: 'Payment',
      action: `User click on Paypal pay button`,
    })

    const body = {
      email: localStorage.getItem('email'),
      paymentId: data.orderID,
    }

    if (
      cart?.item?.shipments[0].method?.type !== 'HOME_DELIVERY' &&
      typeof localStorage.getItem('rmsParcelShopId') !== 'undefined' &&
      localStorage.getItem('rmsParcelShopId') !== null
    ) {
      body['parcelShopId'] = localStorage.getItem('rmsParcelShopId')
    }

    pushInstruction(
      'trackEcommerceOrder',
      cart?.item?.tokenValue,
      numberToPrice(cart?.item?.totals?.total),
      numberToPrice(cart?.item?.totals?.total - cart?.item?.totals?.shipping),
      numberToPrice(cart?.item?.totals?.taxes),
      numberToPrice(cart?.item?.totals?.shipping),
      numberToPrice(cart?.item?.totals?.promotion) // discount (Optional) – Integer/Float/Boolean – Discount offered? Default to false. Otherwise, you should include a numeric value.
    )

    const gaParams = {
      transaction_id: cart?.item?.tokenValue,
      currency: cart.item.currency,
      value: numberToPrice(cart?.item?.totals?.total),
      tax: numberToPrice(cart?.item?.totals?.taxes),
      shipping: numberToPrice(cart?.item?.totals?.shipping),
      items: cart.item.items.map(({ product, quantity, total }) => {
        const firstVariant = Object.values(product?.variants)[0]
        return {
          ['item_name']: product.name,
          ['item_id']: isChalhoub ? firstVariant.sku : product.code,
          ['item_variant']: isChalhoub && firstVariant.code,
          price: numberToPrice(total),
          quantity,
        }
      }),
    }

    const cleanGAParams = clearItemVariantFromGAParamsIfNotChalhoub(gaParams)
    gtag.tracker(GAEvent.PURCHASE, cleanGAParams)

    safeDataLayerPush(getPaymentInfoGtmData(cart, coupon, 'paypal'))

    // Meta Pixel: Purchase event with amount and currency
    if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
      window.fbq('track', 'Purchase', {
        value: numberToPrice(cart?.item?.totals?.total),
        currency: cart?.item?.currency,
      })
    }

    try {
      await createCheckoutPayment(cart.item.tokenValue)
      const captureResponse = await actions.order.capture()
      datadogLogs.logger.info('Payment Successful', captureResponse)
      if (captureResponse && captureResponse?.status === 'COMPLETED') {
        body['paymentResponse'] = captureResponse

        try {
          await postFinalizeDadPayment(body, cart.item.tokenValue)
          await router.push('/confirmation')
        } catch (paymentError) {
          setError('An error occured, please try again')
          datadogLogs.logger.error(
            'Error during payment finalization',
            paymentError
          )
        }
      } else {
        setError('An error occured, please try again')
        datadogLogs.logger.error('Failed to capture order', captureResponse)
      }
    } catch (error) {
      setError('An error occured, please try again')
      datadogLogs.logger.error('Error capturing order', error)
    }
  }

  return (
    <>
      {registeredStoreSlug && (
        <Typography
          variant={'body2'}
          component={'span'}
          className={commonClasses.customCgl}
        >
          <Link href={`/pages/cgl-${registeredStoreSlug}`}>
            <a>
              <CMSText
                data={doc?.data?.custom_cgu_link}
                asText
                defaultText={t('texts:funnel:payment:custom_cgu_link')}
              />
            </a>
          </Link>
        </Typography>
      )}
      {isRetractation && retractReady && (
        <FormControlLabel
          control={
            <Checkbox
              checked={retractAgree}
              onChange={changeRetract}
              name="retractation"
              data-testid={'retract_check'}
            />
          }
          label={
            <Typography
              variant={'body2'}
              component={'span'}
              className={commonClasses.cgu}
            >
              <CMSText
                data={doc?.data?.retractation_agreement}
                defaultText={t('texts:funnel:payment:retractation_agreement')}
              />
            </Typography>
          }
        />
      )}
      <PayPalButtons
        createOrder={createOrder}
        onApprove={onApprove}
        onError={onError}
        style={{
          layout: 'horizontal',
          tagline: false,
          label: 'pay',
        }}
        disabled={!cgu || !retractAgree || isPaypalBlocked}
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={cgu}
            onChange={changeCgu}
            name="checkedA"
            data-testid={'cgu_check'}
          />
        }
        label={
          <Typography
            variant={'body2'}
            component={'span'}
            className={commonClasses.cgu}
          >
            <CMSText
              data={doc?.data?.cgu}
              defaultText={t('texts:funnel:payment:cgu')}
            />
          </Typography>
        }
      />
      {insufficientStockProducts && (
        <Fade in>
          <Box
            textAlign="center"
            mb={2}
            data-testid="paypal-insufficient-stock"
          >
            <Typography
              variant="body2"
              component="p"
              gutterBottom
              color="error"
            >
              {t('texts:funnel:payment:errors:stock_error_intro')}
            </Typography>
            <Box
              component="ul"
              style={{
                margin: '8px 0 12px',
                paddingLeft: '1.25rem',
                display: 'inline-block',
                textAlign: 'left',
              }}
            >
              {insufficientStockProducts.map((name, index) => (
                <Typography
                  key={`${name}-${index}`}
                  component="li"
                  variant="body2"
                  color="error"
                  style={{ marginBottom: 4 }}
                >
                  {name}
                </Typography>
              ))}
            </Box>
            <Typography variant="body2" component="p" color="error">
              {t('texts:funnel:payment:errors:stock_error_footer')}
            </Typography>
          </Box>
        </Fade>
      )}
      {availabilityCheckFailed && (
        <Fade in>
          <Box
            textAlign="center"
            mb={2}
            data-testid="paypal-availability-check-failed"
          >
            <Typography variant="body2" component="p" color="error">
              {t('texts:funnel:payment:errors:availability_check_failed')}
            </Typography>
          </Box>
        </Fade>
      )}
      {error && (
        <Fade in>
          <Typography color="error" variant={'body1'} align={'center'}>
            {error}
          </Typography>
        </Fade>
      )}
    </>
  )
}

const mapStateToProps = (state) => ({
  cart: state.cart,
  stores: state.stores,
})

const mapDispatchToProps = {
  postFinalizeDadPayment: postFinalizeDadPayment,
  createCheckoutPayment: createCheckoutPayment,
  getCheckout: getCheckout,
}

export default connect(mapStateToProps, mapDispatchToProps)(PaymentPaypal)
